import React from 'react';
import { DollarSign, TrendingUp, Package, Building2, Calendar } from 'lucide-react';
import { DataTrustIndicator } from './DataTrustIndicator';
import { MetricCard } from './MetricCard';
import { AssetAllocationChart } from './AssetAllocationChart';
import { AccountsTable } from './AccountsTable';
import { HoldingsTable } from './HoldingsTable';

interface AssetAllocation {
  name: string;
  value: number;
  percentage: number;
  [key: string]: string | number;
}

interface Account {
  account_name: string;
  account_id: string;
  source?: string;
  account_type: string;
  market_value?: number;
}

interface Holding {
  symbol: string;
  security_name: string;
  quantity?: number;
  market_value: number;
}

interface ConflictField {
  field: string;
  field_label: string;
  source_value: string | number;
  new_value?: string | number;
  resolved_value: string | number;
  strategy: string;
  sourceAName?: string;
  sourceBName?: string;
}

interface DataTrust {
  trustPercentage: number;
  sources: string[];
}

interface UnifiedProfileTabProps {
  totalMarketValue: number;
  unrealizedGainLoss?: number;
  holdingsCount: number;
  accountsCount: number;
  lastCalculated: string;
  assetAllocation: AssetAllocation[];
  accounts: Account[];
  holdings: Holding[];
  portfolioAccounts: any[];
  dataTrust: DataTrust;
  conflicts: ConflictField[];
  formatCurrency: (amount: number) => string;
  attributeDefinitionsCount?: number;
  sourceComparisonCount?: number;
}

export const UnifiedProfileTab: React.FC<UnifiedProfileTabProps> = ({
  totalMarketValue,
  unrealizedGainLoss = 0,
  holdingsCount,
  accountsCount,
  lastCalculated,
  assetAllocation,
  accounts,
  holdings,
  portfolioAccounts,
  dataTrust,
  conflicts: _conflicts,
  formatCurrency,
  attributeDefinitionsCount = 0,
  sourceComparisonCount = 0
}) => {
  // Suppress unused variable warning - portfolioAccounts used in dataTransform
  void portfolioAccounts;

  // Format date to show month name instead of numbers
  const formatDateWithMonthName = (dateString: string): string => {
    try {
      // Parse ISO 8601 date string directly (e.g., "2026-05-14T10:01:22.981212Z")
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return dateString; // Return original if parsing fails
      }

      // Format as "Month DD, YYYY" (e.g., "May 14, 2026")
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original if error occurs
    }
  };


  const summaryMetrics = [
    {
      label: 'Total Market Value',
      value: formatCurrency(totalMarketValue),
      icon: DollarSign,
      iconColor: 'text-blue-600'
    },
    {
      label: 'Unrealized Gain/Loss',
      value: formatCurrency(unrealizedGainLoss),
      icon: TrendingUp,
      iconColor: unrealizedGainLoss >= 0 ? 'text-green-600' : 'text-red-600'
    },
    {
      label: 'Holdings Count',
      value: holdingsCount.toString(),
      icon: Package,
      iconColor: 'text-orange-600'
    },
    {
      label: 'Accounts',
      value: accountsCount.toString(),
      icon: Building2,
      iconColor: 'text-indigo-600'
    },
    {
      label: 'Last Calculated',
      value: formatDateWithMonthName(lastCalculated),
      icon: Calendar,
      iconColor: 'text-gray-600'
    }
  ];

  return (
    <div className="space-y-6">
      <DataTrustIndicator
        trustPercentage={dataTrust.trustPercentage}
        sources={dataTrust.sources}
        attributeDefinitionsCount={attributeDefinitionsCount}
        sourceComparisonCount={sourceComparisonCount}
      />

      <div>
        <h2 className="text-xl mb-4 px-3">Relationship Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {summaryMetrics.map((metric, index) => (
            <MetricCard
              key={index}
              label={metric.label}
              value={metric.value}
              icon={metric.icon}
              iconColor={metric.iconColor}
            />
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg mb-4">Asset Allocation</h3>
        <AssetAllocationChart data={assetAllocation} formatCurrency={formatCurrency} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg mb-4">Accounts</h3>
        <AccountsTable accounts={accounts} formatCurrency={formatCurrency} />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg mb-4">Consolidated Holdings</h3>
        <HoldingsTable holdings={holdings} formatCurrency={formatCurrency} />
      </div>
    </div>
  );
};
